import { useContext } from 'react';
import useFetch from 'hooks/api/useFetch';
import { API, TOTAL_QUESTIONNAIRE } from 'constants/global';
import { IActivities, ISectionBaseTexts, IUser, Questionnaire } from 'types/app';
import { AxiosRequestConfig } from 'axios';
import { ToastContext, ToastContextProps } from 'contexts/ToastContext';
import { IHttpResponse } from 'helpers/api';
import { useTranslation } from 'react-i18next';
import { getSectionBaseTexts, getTranslations } from 'helpers/utils';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import { SettingsContext, SettingsContextProps } from 'contexts/SettingsContext';
import { useRollbar } from '@rollbar/react';

export interface IResponse {
  questionId: number;
  questionnaireId: number;
  response: number;
  responseOrder: number;
}

export interface IQuestionnaireData {
  questionnaire?: Questionnaire.IQuestionnaire;
  activities?: IActivities;
  textIntro?: ISectionBaseTexts;
  ipsativeQuestionnaire?: Questionnaire.IIpsativeQuestionnaire;
  gradualQuestionnaire?: Questionnaire.IIpsativeQuestionnaire;
  depuisQuandQuestionnaire?: Questionnaire.IIpsativeQuestionnaire;
}

export interface IQuestionTracker {
  startTime: number;
}

export interface IUseAPIQuestionnaire {
  updateUserInformation: (
    birthYear: string,
    activity: string,
    country: string,
    civility: string,
  ) => Promise<boolean | undefined>;
  sendResponses: (responses: Array<IResponse>, timing?: number) => Promise<boolean>;
  loadQuestionnaireData: (user: IUser) => Promise<IQuestionnaireData>;
  loadIpsativeQuestionnaire: () => Promise<Questionnaire.IIpsativeQuestionnaire | undefined>;
  sendIpsativeResponse: (
    questionnaireId: number,
    questionId: number,
    answerId: number,
    order: number,
    timing?: number,
  ) => Promise<boolean>;
  loadGradualQuestionnaire: () => Promise<Questionnaire.IIpsativeQuestionnaire | undefined>;
  sendGradualResponse: (
    questionnaireId: number,
    questionId: number,
    answerId: number,
    order: number,
    timing?: number,
  ) => Promise<boolean>;
  loadDepuisQuandQuestionnaire: () => Promise<Questionnaire.IIpsativeQuestionnaire | undefined>;
  sendDepuisQuandResponse: (
    questionnaireId: number,
    questionId: number,
    answerId: number,
    order: number,
    timing?: number,
  ) => Promise<boolean>;
  startQuestionTracking: () => void;
  getCurrentTiming: () => number | null;
}

export const DEFAULT_QUESTIONNAIRE: Questionnaire.IQuestionnaire = {
  id: 0,
  total: 0,
  currentQuestionIndex: 0,
  questions: [],
  progression: 0,
};

// Shared module-level tracker (shared across all hook instances)
let currentTracker: IQuestionTracker | null = null;

function useAPIQuestionnaire(): IUseAPIQuestionnaire {
  const { setToast } = useContext(ToastContext) as ToastContextProps;
  const { getRequest, showLoading, hideLoading } = useFetch<any>();
  const { t } = useTranslation('translation');
  const { user } = useContext(UserContext) as UserContextProps;
  const { languages } = useContext(SettingsContext) as SettingsContextProps;
  const rollbar = useRollbar();

  const getTextIntro = async (): Promise<ISectionBaseTexts | undefined> => {
    try {
      const options: AxiosRequestConfig = {
        url: API.ROUTES.QUESTIONNAIRE.INTRO,
        method: 'get',
      };

      const result: IHttpResponse = await getRequest(options);
      const texte = result.data.data?.texte;

      if (texte) {
        const translations = texte?.translations;

        return getSectionBaseTexts(translations, languages, '');
      } else {
        if (result.data.message) {
          setToast({
            message: result.data.message,
            status: 'danger',
          });
        }
      }
    } catch (e: unknown) {
      console.log('Error while getting questionnaire intro texts : ', e);
      setToast({
        message: (e as string) || '',
        status: 'danger',
      });
    }

    return undefined;
  };

  function processCategories(categories: any): IActivities {
    let resultObj: any = {};

    Object.keys(categories).forEach((language) => {
      Object.keys(categories[language]).forEach((categorie: any) => {
        const currentCategory = categories[language][categorie];
        resultObj = {
          ...resultObj,
          [categorie]: {
            code: {
              ...resultObj[categorie]?.code,
              [language]: categorie,
            },
            label: {
              ...resultObj[categorie]?.label,
              [language]: currentCategory,
            },
          },
        };
      });
    });

    return Object.values(resultObj);
  }

  const getActivities = async (): Promise<IActivities | undefined> => {
    try {
      const options: AxiosRequestConfig = {
        url: API.ROUTES.QUESTIONNAIRE.ACTIVITIES,
        method: 'get',
      };

      const result: IHttpResponse = await getRequest(options);
      const categories = result.data.data?.categories;
      if (categories) {
        return processCategories(categories);
      } else {
        if (result.data.message) {
          setToast({
            message: result.data.message,
            status: 'danger',
          });
        }
      }
    } catch (e: unknown) {
      console.log('Error while getting activities list : ', e);
      setToast({
        message: (e as string) || '',
        status: 'danger',
      });
    }

    return undefined;
  };

  const updateUserInformation = async (
    birthYear: string,
    activity: string,
    country: string,
    civility: string,
  ) => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.UPDATE_PROFILE}/${user?.id}`,
        method: 'patch',
        data: {
          userId: user?.id,
          year_birth: birthYear || 0,
          socio_category: activity,
          nationalite: country,
          civilite: civility,
        },
      };

      const result: IHttpResponse = await getRequest(options);
      if (!result.data?.data?.user) {
        setToast({
          message: t('message.error.notupdateduserinfos'),
          status: 'danger',
        });

        return false;
      }
      setToast({
        message: t('message.updateduserinfos'),
        status: 'success',
      });

      return true;
    } catch (e: unknown) {
      console.log('Error while getting activities list : ', e);
      setToast({
        message: t('message.error.notupdateduserinfos'),
        status: 'danger',
      });
      return false;
    } finally {
      hideLoading();
    }
  };

  // Parse questions from questionnaire
  const parseQuestions = (
    questionsList: Array<any>,
    responses: Array<any> = [],
  ): Array<Questionnaire.IQuestion> => {
    const safeResponses = responses || [];
    return questionsList.map((item: any, key: number) => {
      // Look for the user's answer
      const userAnswer = safeResponses.find(
        (ans: any) => ans.Reponse_Version_Question_Question === item.question_id,
      );
      return {
        id: item.question_id,
        label: getTranslations(languages, item.translations, (input) => input.libelle),
        order: item.order || key + 1,
        sectionId: item.group?.id || 0,
        sectionLabel: getTranslations(languages, item.group?.translations, (input) => input.libelle),
        responseMin: 1,
        responseMax: 7,
        responseMinLabel: getTranslations(
          languages,
          item.translations,
          (input) => input.libelle_note_min,
        ),
        responseMaxLabel: getTranslations(
          languages,
          item.translations,
          (input) => input.libelle_note_max,
        ),
        response: userAnswer?.Reponse_Version_Question_ReponseType || undefined,
      };
    });
  };

  const parseIpsativeQuestions = (
    questionsList: Array<any>,
    responses: Array<any> = [],
  ): Array<Questionnaire.IIpsativeQuestion> => {
    const safeResponses = responses || [];
    return questionsList.map((item: any, key: number) => {
      // Look for the user's answer
      const userAnswer = safeResponses.find((ans: any) => ans.question_id === item.id);
      return {
        id: item.id,
        order: item.order || key + 1,
        label: getTranslations(languages, item.translations, (input) => input.libelle),
        group: item.group
          ? {
            id: item.group.id,
            label: getTranslations(languages, item.group.translations, (input) => input.libelle),
          }
          : undefined,
        selectedAnswerId: userAnswer?.response_item_id || undefined,
        answers: item?.response_items?.map((ans: any, ansKey: number) => ({
          id: ans.id,
          order: ans.order || ansKey + 1,
          label: getTranslations(languages, ans.translations, (input) => input.libelle),
        })) ?? [],
      };
    });
  };

  // Load questionnaire
  const getQuestionnaire = async (userId: string, civility = 'Monsieur') => {
    try {
      const questionnaire: Questionnaire.IQuestionnaire = { ...DEFAULT_QUESTIONNAIRE };
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.QUESTIONS}/${civility}`,
        method: 'get',
      };

      const result: IHttpResponse = await getRequest(options);
      const questions = result.data.data?.questions;

      if (questions && Array.isArray(questions)) {
        questionnaire.id = result.data.data.questionnaire?.id;
        questionnaire.total = questions.length;

        // Get questions already answered
        const answersResult = await getRequest({
          url: `${API.ROUTES.QUESTIONNAIRE.USER_ANSWERS}/${userId}`,
          method: 'get',
        });
        const answers = answersResult.data.data?.reponses || [];
        questionnaire.questions = parseQuestions(questions, answers);

        const qAnswered = questionnaire.questions.filter((item) => !!item.response).length;
        questionnaire.progression = Math.floor((qAnswered / TOTAL_QUESTIONNAIRE) * 100);
        if (answersResult.data.data?.current) {
          questionnaire.currentQuestionIndex =
            questionnaire.questions.findIndex(
              (q) => q.order === answersResult.data.data?.current,
            ) || 0;
        }

        return questionnaire;
      } else {
        if (result.data.message) {
          setToast({
            message: result.data.message,
            status: 'danger',
          });
        }
      }
    } catch (e: unknown) {
      setToast({
        message: 'Error loading questionnaire',
        status: 'danger',
      });
    }

    return undefined;
  };

  const sendResponses = async (responses: Array<IResponse>, timing?: number) => {
    showLoading();

    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.ANSWER}/${user?.id}`,
        method: 'post',
        data: {
          answers: responses.map((rep) => ({
            questionsId: rep.questionId,
            response_type: rep.response,
            order: rep.responseOrder,
            questionnaireId: rep.questionnaireId,
            duration: timing !== undefined ? timing : null,
            timestamp: Date.now(),
          })),
        },
      };

      const result: IHttpResponse = await getRequest(options);
      const repState = result.data.data?.reponses;

      if (result.status !== 200 || !repState || repState?.response === null) {
        setToast({
          message: t('questionnaire.answer-not-saved'),
          status: 'danger',
        });
        rollbar.error('Error sending response to server', {
          status: result.status,
          repstate: repState,
          responses: responses,
          message: 'result.status !== 200 || !repState || repState?.response === null',
          user: user,
        });
        return false;
      }

      return !!repState;
    } catch (e: unknown) {
      setToast({
        message: t('questionnaire.answer-not-saved'),
        status: 'danger',
      });
      rollbar.error(
        `Error while sending questionnaire response for user: ${user?.id} ${user?.email} on question: ${responses[0].questionId}`,
        {
          error: e,
          user: {
            id: user?.id,
            email: user?.email,
          },
          responses,
        },
      );
    } finally {
      hideLoading();
    }

    return false;
  };

  const loadQuestionnaireData = async (user: IUser) => {
    const qData: IQuestionnaireData = {};
    showLoading();

    try {
      const userCivility = (user?.civility && user?.civility?.trim() !== '') ? user.civility : 'Monsieur';
      const [questionnaire, ipsativeQuestionnaire, gradualQuestionnaire] = await Promise.all([
        getQuestionnaire(`${user?.id}`, userCivility),
        loadIpsativeQuestionnaire(),
        loadGradualQuestionnaire(),
      ]);

      qData.questionnaire = questionnaire;
      qData.ipsativeQuestionnaire = ipsativeQuestionnaire;
      qData.gradualQuestionnaire = gradualQuestionnaire;

      const isMainComplete = qData.questionnaire?.progression === 100;
      const isGradualComplete = !qData.gradualQuestionnaire || qData.gradualQuestionnaire?.progression === 100;

      if (isMainComplete && isGradualComplete) {
        qData.depuisQuandQuestionnaire = await loadDepuisQuandQuestionnaire();
      }

      if (qData.questionnaire?.id) {
        // Check if not yet done, else do not load other data
        // if (
        //   qData.questionnaire.progression <
        //   (PRIMARY_QUESTIONNAIRE_NB * 100) / TOTAL_QUESTIONNAIRE
        // ) {
        const [textIntro, activities] = await Promise.all([
          getTextIntro(),
          getActivities(),
        ]);
        // Load text intro
        qData.textIntro = textIntro;
        // If user NOT already answered age & activity
        qData.activities = activities;
        // }
      }
    } catch (e: unknown) {
      setToast({
        message: (e as string) || 'Error while loading questionnaire',
        status: 'danger',
      });
    } finally {
      hideLoading();
    }

    return qData;
  };

  const loadIpsativeQuestionnaire = async () => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.IPSATIVE.GET}/${user?.id}`,
        method: 'get',
      };
      const result: IHttpResponse = await getRequest(options);
      const questions = result.data.data?.questions;
      if (questions && Array.isArray(questions)) {
        // Get answers
        const answersResult = await getRequest({
          url: `${API.ROUTES.QUESTIONNAIRE.IPSATIVE.ANSWERS}/${user?.id}`,
          method: 'get',
        });
        const answers = answersResult.data.data?.reponses || [];
        const parsedQuestions = parseIpsativeQuestions(questions, answers);
        const qAnswered = parsedQuestions.filter((item) => !!item.selectedAnswerId).length;

        const resp = {
          id: result.data.data.questionnaire?.id || 0,
          total: questions.length,
          currentQuestionIndex: answersResult.data.data?.current
            ? parsedQuestions.findIndex((q) => q.order === answersResult.data.data?.current)
            : 0,
          progression: Math.floor((qAnswered / questions.length) * 100),
          questions: parsedQuestions,
        };
        if (resp.currentQuestionIndex === -1) resp.currentQuestionIndex = 0;
        return resp;
      } else {
        return undefined;
      }
    } catch (e: unknown) {
      console.error('Error', e);
      rollbar.error(`Error while loading ipsative questionnaire for user: ${user?.id}`, {
        error: e,
      });
    } finally {
      hideLoading();
    }
  };

  const loadGradualQuestionnaire = async () => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.GRADUAL.GET}/${user?.id}`,
        method: 'get',
      };
      const result: IHttpResponse = await getRequest(options);
      const questions = result.data.data?.questions;
      if (questions && Array.isArray(questions)) {
        // Get answers
        const answersResult = await getRequest({
          url: `${API.ROUTES.QUESTIONNAIRE.GRADUAL.ANSWERS_LIST}/${user?.id}`,
          method: 'get',
        });
        const answers = answersResult.data.data?.reponses || [];
        const parsedQuestions = parseIpsativeQuestions(questions, answers);
        const qAnswered = parsedQuestions.filter((item) => !!item.selectedAnswerId).length;

        const resp = {
          id: result.data.data.questionnaire?.id || 0,
          total: questions.length,
          currentQuestionIndex: answersResult.data.data?.current
            ? parsedQuestions.findIndex((q) => q.order === answersResult.data.data?.current)
            : 0,
          progression: Math.floor((qAnswered / questions.length) * 100),
          questions: parsedQuestions,
        };
        if (resp.currentQuestionIndex === -1) resp.currentQuestionIndex = 0;
        return resp;
      } else {
        return undefined;
      }
    } catch (e: unknown) {
      console.error('Error', e);
      rollbar.error(`Error while loading gradual questionnaire for user: ${user?.id}`, {
        error: e,
      });
    } finally {
      hideLoading();
    }
  };

  const sendIpsativeResponse = async (questionnaireId: number, questionId: number, answerId: number, order: number, timing?: number) => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.IPSATIVE.ANSWER}/${user?.id}`,
        method: 'post',
        data: {
          answers: [
            {
              reponseItemId: answerId,
              initialReponseItemId: answerId,
              questionId: questionId,
              order: order,
              questionnaireId: questionnaireId,
              duration: timing !== undefined ? timing : null,
              timestamp: Date.now(),
            },
          ],
        },
      };

      const result: IHttpResponse = await getRequest(options);
      const repState = result.data.data?.reponses;

      if (result.status !== 200 || !repState) {
        setToast({
          message: t('questionnaire.answer-not-saved'),
          status: 'danger',
        });
        rollbar.error('Error sending ipsative response to server', {
          status: result.status,
          repstate: repState,
          user: user,
          questionId,
          answerId,
        });
        return false;
      }

      return true;
    } catch (e: unknown) {
      setToast({
        message: t('questionnaire.answer-not-saved'),
        status: 'danger',
      });
      rollbar.error(`Error while sending ipsative response for user: ${user?.id}`, {
        error: e,
      });
      return false;
    } finally {
      hideLoading();
    }
  };

  const sendGradualResponse = async (questionnaireId: number, questionId: number, answerId: number, order: number, timing?: number) => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.GRADUAL.ANSWER}/${user?.id}`,
        method: 'post',
        data: {
          answers: [
            {
              reponseItemId: answerId,
              initialReponseItemId: answerId,
              questionId: questionId,
              order: order,
              questionnaireId: questionnaireId,
              duration: timing !== undefined ? timing : null,
              timestamp: Date.now(),
            },
          ],
        },
      };

      const result: IHttpResponse = await getRequest(options);
      const repState = result.data.data?.reponses;

      if (result.status !== 200 || !repState) {
        setToast({
          message: t('questionnaire.answer-not-saved'),
          status: 'danger',
        });
        rollbar.error('Error sending gradual response to server', {
          status: result.status,
          repstate: repState,
          user: user,
          questionId,
          answerId,
        });
        return false;
      }

      return true;
    } catch (e: unknown) {
      setToast({
        message: t('questionnaire.answer-not-saved'),
        status: 'danger',
      });
      rollbar.error(`Error while sending gradual response for user: ${user?.id}`, {
        error: e,
      });
      return false;
    } finally {
      hideLoading();
    }
  };

  const loadDepuisQuandQuestionnaire = async () => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.DEPUIS_QUAND.GET}/${user?.id}`,
        method: 'get',
      };
      const result: IHttpResponse = await getRequest(options);
      const questions = result.data.data?.questions;
      if (questions && Array.isArray(questions)) {
        // Get answers
        const answersResult = await getRequest({
          url: `${API.ROUTES.QUESTIONNAIRE.DEPUIS_QUAND.ANSWERS}/${user?.id}`,
          method: 'get',
        });
        const answers = answersResult.data.data?.reponses || [];
        const parsedQuestions = parseIpsativeQuestions(questions, answers);
        const qAnswered = parsedQuestions.filter((item) => !!item.selectedAnswerId).length;

        const resp = {
          id: result.data.data.questionnaire?.id || 0,
          total: questions.length,
          currentQuestionIndex: answersResult.data.data?.current
            ? parsedQuestions.findIndex((q) => q.order === answersResult.data.data?.current)
            : 0,
          progression: Math.floor((qAnswered / questions.length) * 100),
          questions: parsedQuestions,
        };
        if (resp.currentQuestionIndex === -1) resp.currentQuestionIndex = 0;
        return resp;
      } else {
        return undefined;
      }
    } catch (e: unknown) {
      console.error('Error', e);
      rollbar.error(`Error while loading depuis quand questionnaire for user: ${user?.id}`, {
        error: e,
      });
    } finally {
      hideLoading();
    }
  };

  const sendDepuisQuandResponse = async (questionnaireId: number, questionId: number, answerId: number, order: number, timing?: number) => {
    showLoading();
    try {
      const options: AxiosRequestConfig = {
        url: `${API.ROUTES.QUESTIONNAIRE.DEPUIS_QUAND.ANSWER}/${user?.id}`,
        method: 'post',
        data: {
          answers: [
            {
              reponseItemId: answerId,
              initialReponseItemId: answerId,
              questionId: questionId,
              order: order,
              questionnaireId: questionnaireId,
              duration: timing !== undefined ? timing : null,
              timestamp: Date.now(),
            },
          ],
        },
      };

      const result: IHttpResponse = await getRequest(options);
      const repState = result.data.data?.reponses;

      if (result.status !== 200 || !repState) {
        setToast({
          message: t('questionnaire.answer-not-saved'),
          status: 'danger',
        });
        rollbar.error('Error sending depuis quand response to server', {
          status: result.status,
          repstate: repState,
          user: user,
          questionId,
          answerId,
        });
        return false;
      }

      return true;
    } catch (e: unknown) {
      setToast({
        message: t('questionnaire.answer-not-saved'),
        status: 'danger',
      });
      rollbar.error(`Error while sending depuis quand response for user: ${user?.id}`, {
        error: e,
      });
      return false;
    } finally {
      hideLoading();
    }
  };

  // Timing tracking functions
  const startQuestionTracking = () => {
    // console.log('Starting question tracking', Date.now());
    currentTracker = {
      startTime: Date.now(),
    };
  };

  const getCurrentTiming = (): number | null => {
    // console.log('Getting current timing', Date.now(), currentTracker);
    // console.log('Current tracker start time', currentTracker?.startTime);
    // console.log('Current timing', currentTracker ? Date.now() - currentTracker.startTime : null);
    if (!currentTracker) return null;
    return Date.now() - currentTracker.startTime;
  };

  return {
    updateUserInformation,
    loadQuestionnaireData,
    sendResponses,
    loadIpsativeQuestionnaire,
    sendIpsativeResponse,
    loadGradualQuestionnaire,
    sendGradualResponse,
    loadDepuisQuandQuestionnaire,
    sendDepuisQuandResponse,
    startQuestionTracking,
    getCurrentTiming,
  };
}

export default useAPIQuestionnaire;
